import React, { useCallback } from 'react';
import { PrimaryButton } from '@components/atoms';
import { useSetRecoilState } from 'recoil';
import { dialogContentsState } from '@components/templates/Dialog';
import { ErrorCode, ErrorMessage } from '@variables/error';
import { getFetcher } from '../App';
import { DicomInfo } from '@components/../pages/SW/Metatag';
import _ from 'lodash';
import { REGEX_ONLY_NUMBER, REGEX_ONLY_NUMBER_AND_VERTICAL_BAR } from '@components/templates/Fields/MyAccount';

const SettingCommonMethods = () => {
  const setDialogContents = useSetRecoilState(dialogContentsState);

  /**
   * metaList 추가
   */
  const makeMetaList = (metaList: DicomInfo[], setMetaList: (_data: any) => void) => {
    const _tmp = {
      action: 'IGNORE',
      method: 'EQUALS',
      definition: '',
      isNumericVR: false,
      isSequential: false,
      isChanged: true,
      tag: '',
      value: '',
      dataset: '',
      vr: '',
      valid: true,
    };
    const _data = JSON.parse(JSON.stringify(metaList));
    _data.push(_tmp);
    setMetaList(_data);
  };

  /**
   * metaList 삭제
   */
  const delMetaList = (idx: number, metaList: DicomInfo[], setMetaList: (_data: any) => void) => {
    const _data = JSON.parse(JSON.stringify(metaList));
    _data.splice(idx, 1);
    setMetaList(_data);
  };

  /**
   * metaList ui valid 상태 클래스 반환
   * @param value
   */
  const metaListStatus = (value: DicomInfo) => {
    let className = '';
    if (!value.tag || value.value ? !value.value : !value.dataset) {
      className = 'disable';
    } else if (value.tag && (value.value ? value.value : value.dataset) && !value.valid) {
      className = 'error';
    } else if (value.tag && value.value ? value.value : value.dataset) {
      className = 'active';
    }
    return className;
  };

  /**
   * metaList 로컬 유효성 체크
   * 중복 체크
   */
  const metaListLocalValid = (idx: number, metaList: DicomInfo[]) => {
    // 확인 성공한 내용 있는지 중복 체크
    let duplicateCount = 0;
    metaList.forEach((data, index) => {
      if (data.valid && data.tag === metaList[idx].tag && idx !== index) duplicateCount = duplicateCount + 1;
    });

    if (duplicateCount > 0) {
      setDialogContents({
        type: 'plain',
        body: `Already registered items.`,
        footer: (
          <PrimaryButton
            onClick={async () => {
              setDialogContents({ type: 'hidden' });
            }}
          >
            OK
          </PrimaryButton>
        ),
      });
      return false;
    } else {
      return true;
    }
  };

  /**
   * metaList 변경 또는 유효성 결과 값 적용
   * @param e
   * @param index
   * @param type
   */
  const metaListChangeData = useCallback(
    (e: any, index: number, type: string, metaList: any, originMetaList: any, setMetaList: (_data: any) => void) => {
      const _originData = JSON.parse(JSON.stringify(originMetaList));
      const _data = JSON.parse(JSON.stringify(metaList));

      // 변경시 상태 초기화
      if (type !== 'VALID') {
        _data[index].isChanged = true;
        _data[index].valid = true;
      }

      switch (type) {
        case 'VALID':
          _data[index].valid = e === '200';
          if (e === '200') _originData[index] = _data[index]; // valid 성공시 백업 데이터 동기화
          break;
        case 'TAG':
          _data[index].dicomTagId = e.id;
          _data[index].definition = e.definition;
          _data[index].isNumericVR = e.vr === 'IS';
          _data[index].isSequential = e.vr === 'IS';
          _data[index].tag = e.tag.replace(',', '');
          _data[index].dataset = '';
          break;
        case 'VALUE':
          if (_data[index].isNumericVR) {
            _data[index].value = e.target.value.replace(REGEX_ONLY_NUMBER, '');
          } else {
            _data[index].value = e.target.value;
          }
          break;
        case 'DATASET':
          if (_data[index].isNumericVR && _data[index].method === 'IN') {
            _data[index].dataset = e.target.value.replace(REGEX_ONLY_NUMBER_AND_VERTICAL_BAR, '');
          } else if(_data[index].isNumericVR) {
            _data[index].dataset = e.target.value.replace(REGEX_ONLY_NUMBER, '');
          } else {
            _data[index].dataset = e.target.value;
          }
          break;
        case 'SEQUENCE':
          _data[index].isSequential = e.target.checked;
          break;
        case 'ACTION':
          _data[index].action = e.value;
          break;
        case 'METHOD':
          _data[index].method = e.value;
          break;
      }

      // 변경 확인
      if (_originData[index]) {
        _data[index].isChanged =
          _data[index].tag !== _originData[index].tag ||
          _data[index].dataset !== _originData[index].dataset ||
          _data[index].isSequential !== _originData[index].isSequential ||
          _data[index].value !== _originData[index].value;
      } else {
        _data[index].isChanged = true;
      }

      setMetaList(_data);
    },
    [],
  );

  /**
   * dicom tag 가져오기
   */
  const getDicomTagAll = async () => {
    const url = `api/dicom-tag`;
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    const _result = await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          return r;
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });

    return _result;
  };

  /**
   * Fetch 에러 핸들러
   * @param r
   */
  const errorHandler = (r: any) => {
    let header = '';
    let body = '';
    if (ErrorCode[r.code]) {
      header = `${ErrorCode[r.code]}`;
      body = `${ErrorMessage[ErrorCode[r.code]]}`;
    } else {
      header = r.code;
    }

    setDialogContents({
      type: 'error',
      header: `${header}`,
      body: <>{body}</>,
      footer: (
        <PrimaryButton
          onClick={() => {
            if (r.code === 'UNAUTHORIZED') location.replace('/');
            setDialogContents({ type: 'hidden' });
          }}
        >
          OK
        </PrimaryButton>
      ),
    });
  };

  const checkValidAllClear = useCallback((metaList: DicomInfo[], originMetaList: DicomInfo[]) => {
    // send가 true 이면 현재 변경 데이터 확인
    const sameData = _.isEqual(metaList, originMetaList);

    if (!sameData) {
      let count = 0;
      //백업 데이터와 같지 않으면 // 현재 메타 데이터에 값이 다 있고,
      // valid가 트루 이고  // isChanged가 false인것만 있냐?
      metaList.forEach((data) => {
        if (!data.valid || data.isChanged || !data.tag || !data.definition) count = count + 1;
      });

      return count === 0;
    } else {
      // 백업 데이터와 같으면 그냥 false
      return false;
    }
  }, []);

  return {
    makeMetaList,
    delMetaList,
    metaListStatus,
    metaListLocalValid,
    metaListChangeData,
    getDicomTagAll,
    errorHandler,
    checkValidAllClear,
  };
};

export default SettingCommonMethods;
